import React, { createContext, useContext, useEffect, useState } from "react";
import supabase from "@/lib/supabase";

interface AuthCtx {
  user: any | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
}

const Ctx = createContext<AuthCtx>({
  user: null, loading: true,
  signIn: async () => {}, signOut: async () => {}, signUp: async () => {},
  resetPassword: async () => {}, updatePassword: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const sb = supabase();
    sb.auth.getSession().then(({ data }) => {
      setUser(data.session?.user ?? null);
      setLoading(false);
    });
    const { data: sub } = sb.auth.onAuthStateChange((_e, session) => {
      setUser(session?.user ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const _restAuth = async (path: string, body: any) => {
    const base = (process.env.REACT_APP_SUPABASE_URL || "").replace(/\/$/, "");
    const key = process.env.REACT_APP_SUPABASE_ANON_KEY || "";
    const res = await fetch(`${base}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", apikey: key, Authorization: `Bearer ${key}` },
      body: JSON.stringify(body),
    });
    let data: any = null;
    try { data = await res.json(); } catch { /* empty */ }
    if (!res.ok) {
      const detail = data?.error_description || data?.msg || data?.error || data?.message || `Auth failed (${res.status})`;
      throw new Error(detail);
    }
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const data = await _restAuth("/auth/v1/token?grant_type=password", { email, password });
    if (data?.access_token) {
      await supabase().auth.setSession({ access_token: data.access_token, refresh_token: data.refresh_token });
      setUser(data.user || null);
    }
  };
  const signUp = async (email: string, password: string) => {
    const redirectTo = `${window.location.origin}/admin/reset-password`;
    await _restAuth("/auth/v1/signup", { email, password, options: { emailRedirectTo: redirectTo } });
  };
  const signOut = async () => {
    await supabase().auth.signOut();
  };
  const resetPassword = async (email: string) => {
    const redirectTo = `${window.location.origin}/admin/reset-password`;
    // Bypass the SDK to avoid a known "body stream already read" bug on
    // certain SDK versions when the new sb_publishable_* anon key is used.
    // IMPORTANT: GoTrue's /recover endpoint only honours the redirect as a
    // ?redirect_to= QUERY PARAM — passing redirectTo in the JSON body is
    // silently ignored, which makes the link fall back to the dashboard Site URL.
    // The redirect URL must also be allow-listed in
    // Supabase → Authentication → URL Configuration → Redirect URLs.
    const base = (process.env.REACT_APP_SUPABASE_URL || "").replace(/\/$/, "");
    const key = process.env.REACT_APP_SUPABASE_ANON_KEY || "";
    const url = `${base}/auth/v1/recover?redirect_to=${encodeURIComponent(redirectTo)}`;
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", apikey: key, Authorization: `Bearer ${key}` },
      body: JSON.stringify({ email, gotrue_meta_security: {} }),
    });
    if (!res.ok) {
      let detail = "";
      try { const j = await res.json(); detail = j?.error_description || j?.msg || j?.error || j?.message || ""; } catch { /* ignore */ }
      throw new Error(detail || `Reset failed (${res.status})`);
    }
  };
  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase().auth.updateUser({ password: newPassword });
    if (error) throw error;
  };

  return <Ctx.Provider value={{ user, loading, signIn, signOut, signUp, resetPassword, updatePassword }}>{children}</Ctx.Provider>;
};

export const useAuth = () => useContext(Ctx);
